// Cross-cutting UI strings shared by more than one domain or feature area.
//
// This is the home for strings that belong to no single domain -- generic
// actions ('Reset'), empty/error states ('No data', the report load failure),
// and the chrome of shared components (filter panels, grid and chart
// containers). Keys that are meaningful only inside one domain still belong in
// that domain's file.
//
// `common` is registered FIRST in the `domains` array in `src/i18n/index.js`,
// so it acts as a base layer: a domain file that defines the same key
// deliberately overrides the shared default rather than silently colliding
// with it.
export default {
  en: {
    // Generic actions
    reset: 'Reset',

    // Empty / error states
    'no-data': 'No data',
    'an-error-occurred-while-loading-the-report':
      'An error occurred while loading the report. Please reduce the stores or date range. If the problem is not resolved, please contact support.',

    // Filter containers
    filters: 'Filters',
    'please-select-at-least-one-location': 'Please select at least one location',
    'please-select-a-scope': 'Please select a scope',
    'please-select-a-date-range': 'Please select a date range',
    'please-select-a-time-segment': 'Please select a time segment',
    'please-select-please-select-at-least-one-daypart':
      'Please select at least one daypart',
    'please-select-please-select-at-least-one-time-slice':
      'Please select at least one time slice',
    'please-select-please-select-at-least-one-peak':
      'Please select at least one peak',
    'the-following-restaurants-are-not-included':
      'The following restaurants are not included in this report because they are not included in your user profile.',
    'if-you-are-logged-in-with-eid':
      'If you are logged in with an eID, your restaurant list must be corrected in GAM. Once the restaurants are added, you will need to log out and back in for QSRSoft to retrieve the updated restaurant list. GAM changes may take as long as 48 hours to propagate to our system.',
    'if-you-are-logged-in-with-email':
      'If you are logged in with an email, escalate this to your System Admin so they can add the restaurants to your profile. You will need to refresh your browser after the Sys Admin completes the change.',

    // Grid container
    'expand-rows': 'Expand Rows',

    // Chart container
    overlap: 'Overlap',
    showChart: 'Show chart',
    hideChart: 'Hide chart',
  },
  es: {
    // Generic actions
    // Must match myq-core's standardTranslations: core's useI18n spreads those
    // over the domain messages this file contributes, so a different value here
    // would render only in global scope ($t / i18n.global.t) and disagree with
    // every component that uses the useI18n composable.
    reset: 'Restablecer',

    // Empty / error states
    'no-data': 'Sin datos',
    'an-error-occurred-while-loading-the-report':
      'Se produjo un error al cargar el informe. Reduzca las tiendas o el rango de fechas. Si el problema no se resuelve, comuníquese con el soporte.',

    // Filter containers
    filters: 'Filtros',
    'please-select-at-least-one-location':
      'Por favor seleccione al menos una ubicación',
    'please-select-a-scope': 'Por favor seleccione un alcance',
    'please-select-a-date-range': 'Por favor seleccione un rango de fechas',
    'please-select-a-time-segment': 'Por favor seleccione un segmento de tiempo',
    'please-select-please-select-at-least-one-daypart':
      'Por favor seleccione al menos un día',
    'please-select-please-select-at-least-one-time-slice':
      'Por favor seleccione al menos una franja horaria',
    'please-select-please-select-at-least-one-peak':
      'Por favor seleccione al menos un pico',
    'the-following-restaurants-are-not-included':
      'Los siguientes restaurantes no están incluidos en este informe porque no están incluidos en su perfil de usuario.',
    'if-you-are-logged-in-with-eid':
      'Si ha iniciado sesión con un eID, su lista de restaurantes debe corregirse en GAM. Una vez que se agreguen los restaurantes, deberá cerrar sesión y volver a iniciar sesión para que QSRSoft recupere la lista actualizada de restaurantes. Los cambios en GAM pueden tardar hasta 48 horas en propagarse a nuestro sistema.',
    'if-you-are-logged-in-with-email':
      'Si ha iniciado sesión con un correo electrónico, comuníquese con su administrador del sistema para que agregue los restaurantes a su perfil. Deberá actualizar su navegador después de que el administrador complete el cambio.',

    // Grid container
    'expand-rows': 'Expandir Filas',

    // Chart container
    overlap: 'Superponer',
    showChart: 'Mostrar gráfico',
    hideChart: 'Ocultar gráfico',
  },
  fr: {
    // Generic actions
    reset: 'Réinitialiser',

    // Empty / error states
    'no-data': 'Aucune donnée',
    'an-error-occurred-while-loading-the-report':
      'Une erreur s’est produite lors du chargement du rapport. Veuillez réduire le nombre de restaurants ou la plage de dates. Si le problème persiste, veuillez communiquer avec le soutien.',

    // Filter containers
    filters: 'Filtres',
    'please-select-at-least-one-location':
      'Veuillez sélectionner au moins un emplacement',
    'please-select-a-scope': 'Veuillez sélectionner une portée',
    'please-select-a-date-range': 'Veuillez sélectionner une plage de dates',
    'please-select-a-time-segment': 'Veuillez sélectionner un segment horaire',
    'please-select-please-select-at-least-one-daypart':
      'Veuillez sélectionner au moins une période de la journée',
    'please-select-please-select-at-least-one-time-slice':
      'Veuillez sélectionner au moins une plage horaire',
    'please-select-please-select-at-least-one-peak':
      'Veuillez sélectionner au moins une pointe',
    'the-following-restaurants-are-not-included':
      'Les restaurants suivants ne sont pas inclus dans ce rapport, car ils ne figurent pas dans votre profil d’utilisateur.',
    'if-you-are-logged-in-with-eid':
      'Si vous êtes connecté avec un eID, votre liste de restaurants doit être corrigée dans GAM. Une fois les restaurants ajoutés, vous devrez vous déconnecter puis vous reconnecter pour que QSRSoft récupère la liste à jour. Les modifications apportées dans GAM peuvent prendre jusqu’à 48 heures pour se propager à notre système.',
    'if-you-are-logged-in-with-email':
      'Si vous êtes connecté avec une adresse courriel, transmettez la demande à votre administrateur système afin qu’il ajoute les restaurants à votre profil. Vous devrez actualiser votre navigateur une fois la modification effectuée.',

    // Grid container
    'expand-rows': 'Développer les lignes',

    // Chart container
    overlap: 'Superposer',
    showChart: 'Afficher le graphique',
    hideChart: 'Masquer le graphique',
  },
  'fr-ca': {
    // Generic actions
    reset: 'Réinitialiser',

    // Empty / error states
    'no-data': 'Aucune donnée',
    'an-error-occurred-while-loading-the-report':
      'Une erreur s’est produite lors du chargement du rapport. Veuillez réduire le nombre de restaurants ou la plage de dates. Si le problème persiste, veuillez communiquer avec le soutien.',

    // Filter containers
    filters: 'Filtres',
    'please-select-at-least-one-location':
      'Veuillez sélectionner au moins un emplacement',
    'please-select-a-scope': 'Veuillez sélectionner une portée',
    'please-select-a-date-range': 'Veuillez sélectionner une plage de dates',
    'please-select-a-time-segment': 'Veuillez sélectionner un segment horaire',
    'please-select-please-select-at-least-one-daypart':
      'Veuillez sélectionner au moins une période de la journée',
    'please-select-please-select-at-least-one-time-slice':
      'Veuillez sélectionner au moins une plage horaire',
    'please-select-please-select-at-least-one-peak':
      'Veuillez sélectionner au moins une pointe',
    'the-following-restaurants-are-not-included':
      'Les restaurants suivants ne sont pas inclus dans ce rapport, car ils ne figurent pas dans votre profil d’utilisateur.',
    'if-you-are-logged-in-with-eid':
      'Si vous êtes connecté avec un eID, votre liste de restaurants doit être corrigée dans GAM. Une fois les restaurants ajoutés, vous devrez vous déconnecter puis vous reconnecter pour que QSRSoft récupère la liste à jour. Les modifications apportées dans GAM peuvent prendre jusqu’à 48 heures pour se propager à notre système.',
    'if-you-are-logged-in-with-email':
      'Si vous êtes connecté avec une adresse courriel, transmettez la demande à votre administrateur système afin qu’il ajoute les restaurants à votre profil. Vous devrez actualiser votre navigateur une fois la modification effectuée.',

    // Grid container
    'expand-rows': 'Développer les lignes',

    // Chart container
    overlap: 'Superposer',
    showChart: 'Afficher le graphique',
    hideChart: 'Masquer le graphique',
  },
};
